import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ServiceProductionClient } from "../../api/floor";
import { formatNumber } from "../../api/manufacturing";
import type { ProductionOrderDTO } from "../../api/production";
import { OpenRecord } from "../../components/OpenRecord";
import { ProductionStatusBadge } from "../production/badges";

/** A thin progress bar with its percentage, for a lot's floor progress. */
export function ProgressBar({ value }: { value: number | null | undefined }) {
  const percent = Math.max(0, Math.min(100, Math.round(Number(value ?? 0) * 100)));
  return (
    <span className="inline-flex min-w-28 items-center gap-2">
      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-surface-sunk">
        <span className={`block h-full ${percent >= 100 ? "bg-ok" : "bg-accent"}`} style={{ width: `${String(percent)}%` }} />
      </span>
      <span className="text-[11px] tabular-nums text-ink-muted">{percent}%</span>
    </span>
  );
}

/**
 * The service lots making this order's lines: one row per lot, with where it
 * is on the floor. A lot can make lines of several service orders, so its
 * quantity may be more than this order asked for.
 */
export function ServiceProductionTab({ orderId }: { orderId: number }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ServiceProductionClient(http), [http]);
  const lots = useQuery({
    queryKey: ["servicedesk", "production", "order", orderId],
    queryFn: () => client.lotsForOrder(orderId),
  });

  const cols = useMemo(() => {
    const c = defineColumns<ProductionOrderDTO>();
    return [
      c.number("number", (row) => row.number, { headerKey: "column.mo.number", priority: 1 }),
      c.custom("status", (row) => row.status, (row) => <ProductionStatusBadge status={row.status} />, { headerKey: "column.status", priority: 1 }),
      c.custom("progress", (row) => row.progress ?? 0, (row) => <ProgressBar value={row.progress} />, {
        headerKey: "column.mo.progress",
        priority: 2,
      }),
      c.text(
        "components",
        (row) => `${formatNumber(row.component_done_count ?? 0)} / ${formatNumber(row.component_count ?? 0)}`,
        { headerKey: "column.mo.components-done", priority: 3 },
      ),
      c.text("quantity", (row) => formatNumber(row.quantity), { headerKey: "column.mo.quantity", priority: 3 }),
      c.text("due_date", (row) => row.due_date ?? "", { headerKey: "column.mo.due", priority: 2 }),
      c.text("name", (row) => row.name ?? "", { headerKey: "column.mo.name", priority: 4 }),
    ];
  }, [t]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<ProductionOrderDTO>
        data={lots.data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={lots.isLoading}
        rowActions={(row) => <OpenRecord target={{ kind: "production-order", id: row.id }} label={String(row.number)} />}
        emptyMessage={t("sd.plant.no-lots", "Nothing on this order is being made at the plant.")}
      />
    </div>
  );
}
